import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import { primaryColor } from "../../../../utils/comon";
import { residentialVisitorNotificationService } from "../../../../services/residential/visitorNotificationService";
import apiServices from "../../../../services/apiService"; // generic api service

const NotificationUsersManage = () => {
    const { user } = useSelector((state) => state.auth);
    const unitId = user?.branchorunitID || user?.unitId;
    const actionByUserId = user?.userId || user?.id;

    const [loading, setLoading] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const fetchAllUsersInUnit = async () => {
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ResUserDetails/getUserDetails?PageNumber=1&PageSize=100&unitId=${unitId}`
            });

            if (response?.success) {
                const resData = response?.data?.Resuserdetailsdata || response?.data?.Data || response?.data?.data || response?.data || [];
                const dataArray = Array.isArray(resData) ? resData : [];

                const users = dataArray.map(u => ({
                    id: u.Id || u.UserId || u.userId || u.id,
                    name: u.FullName || (u.FirstName ? `${u.FirstName} ${u.LastName || ''}`.trim() : null) || u.fullName || u.firstName || "Unknown User",
                    email: u.Email || u.email || u.Mobile || u.mobile || "No Contact Info",
                    role: u.RoleName || u.roleName || "User"
                }));
                setAllUsers(users);
            } else {
                setAllUsers([]);
            }
        } catch (error) {
            console.error("Failed to fetch users", error);
            setAllUsers([]);
        }
    };

    const fetchActiveNotificationUsers = async () => {
        if (!unitId) return;
        try {
            setLoading(true);
            const response = await residentialVisitorNotificationService.getNotificationUsers(unitId);
            if (response?.data?.isSuccess && response?.data?.activeUserIds) {
                setSelectedUserIds(response.data.activeUserIds);
            }
        } catch (error) {
            console.error("Failed to fetch notification settings", error);
            toast.error("Failed to load active notification settings.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (unitId) {
            fetchAllUsersInUnit();
            fetchActiveNotificationUsers();
        }
    }, [unitId]);

    const handleToggleUser = (id) => {
        setSelectedUserIds((prev) => 
            prev.includes(id) ? prev.filter((userId) => userId !== id) : [...prev, id]
        );
    };

    const handleSave = async () => {
        if (!unitId || !actionByUserId) {
            toast.error("Missing Unit ID or User ID for this action.");
            return;
        }

        try {
            setLoading(true);
            const payload = {
                unitId: unitId,
                userIds: selectedUserIds,
                actionByUserId: actionByUserId
            };

            const response = await residentialVisitorNotificationService.manageNotificationUsers(payload);
            if (response?.data?.isSuccess || response?.success) {
                toast.success("Notification settings updated successfully!");
                fetchActiveNotificationUsers(); // Refresh
            } else {
                toast.error(response?.data?.message || "Failed to update notification settings.");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-2 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Notification Receivers"
                subHeading="Manage which users will receive high-priority alerts when a visitor arrives at the gate."
            />

            <div className="flex-1">
                <div className="bg-cardbg rounded-xl shadow-sm border border-bordercolor p-6 max-w-4xl">
                    <div className="mb-6 flex justify-between items-center border-b border-bordercolor pb-4">
                        <div>
                            <h2 className="text-lg font-semibold text-textprimary">Select Receivers</h2>
                            <p className="text-sm text-textsecondary mt-1">
                                Check the box next to users who should receive visitor arrival notifications.
                            </p>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: primaryColor() }}></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {allUsers.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {allUsers.map((user) => (
                                        <label 
                                            key={user.id} 
                                            className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${selectedUserIds.includes(user.id) ? 'bg-[#004B590A] border-[#004B59]' : 'hover:bg-dashboardbg border-bordercolor'}`}
                                        >
                                            <div className="flex items-center h-5">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUserIds.includes(user.id)}
                                                    onChange={() => handleToggleUser(user.id)}
                                                    className="w-4 h-4 text-[#004B59] bg-cardbg border-bordercolor rounded focus:ring-[#004B59]"
                                                    style={{ accentColor: primaryColor() }}
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <span className="font-medium text-textprimary block">{user.name}</span>
                                                <span className="text-textsecondary block">{user.email}</span>
                                                {user.role && <span className="text-xs bg-dashboardbg text-textsecondary px-2 py-0.5 rounded mt-1 inline-block">{user.role}</span>}
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-textsecondary">
                                    No users found in this unit.
                                </div>
                            )}

                            <div className="pt-6 mt-6 border-t border-bordercolor flex justify-end">
                                <button
                                    onClick={handleSave}
                                    disabled={loading}
                                    className="px-6 py-2.5 rounded-lg text-white font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                                    style={{ 
                                        backgroundColor: primaryColor(),
                                        opacity: loading ? 0.7 : 1 
                                    }}
                                >
                                    {loading ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationUsersManage;
